import type { DropApiOperation } from '@domain/schemas/api-patch-operation';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { PatchResponseWithId } from '@responses/common';
import type {
  CreateEvalConfigPayload,
  PublishEvalConfigDraftPayload,
  UpdateEvalConfigDraftPayload,
  UpdateEvalConfigMetaPayload,
} from '@root/domain/schemas/payload.schema';
import axios from 'axios';

/**
 * Eval config mutations — Contract Review Bot evaluation period. See
 * src/queries/evalConfigs.ts for the read side. The whole feature is
 * isolated so it can be removed in a single pass after the eval.
 */

export const createEvalConfig = async (payload: CreateEvalConfigPayload): Promise<PatchResponseWithId> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'post',
    url: '/eval_configs',
    data: payload,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const updateEvalConfigMeta = async (
  id: string,
  payload: DropApiOperation<UpdateEvalConfigMetaPayload>,
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/eval_configs/${id}`,
    data: {
      operation: ApiPatchOperation.UpdateEvalConfigMeta,
      ...payload,
    },
  });
  await axios(networkPayload);
};

export const updateEvalConfigDraft = async (
  id: string,
  payload: DropApiOperation<UpdateEvalConfigDraftPayload>,
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/eval_configs/${id}`,
    data: {
      operation: ApiPatchOperation.UpdateEvalConfigDraft,
      ...payload,
    },
  });
  await axios(networkPayload);
};

/**
 * Promotes a config to champion — the only config brokers see runs of. The
 * backend clears the flag from the previous champion in the same transaction,
 * so callers only need to invalidate the configs list afterwards.
 */
export const setChampionEvalConfig = async (id: string): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/eval_configs/${id}`,
    data: {
      operation: ApiPatchOperation.SetChampionEvalConfig,
    },
  });
  await axios(networkPayload);
};

export const publishEvalConfigDraft = async (
  id: string,
  payload: DropApiOperation<PublishEvalConfigDraftPayload> = {},
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/eval_configs/${id}`,
    data: {
      operation: ApiPatchOperation.PublishEvalConfigDraft,
      ...payload,
    },
  });
  await axios(networkPayload);
};
